import React, { useState } from 'react';
import styled from 'styled-components';
import Text from 'components/atoms/Text';
import Button from 'components/atoms/Button';
import Input from 'components/atoms/Input';
import Icon from 'components/atoms/Icon';
import { Spinner, COLOR_SYSTEM } from 'components/atoms';
import { NhisStatus, AuthMethodType, NHIS_DETAIL_MESSAGES, NHIS_DETAIL_ACTIONS } from 'types/nhis';
import { confirmNhisAuth } from 'api/nhisService';
import { ToastManager, ConfirmManager } from 'components/organisms/CommonUI';
import { log, LogLevel } from 'utils/LogUtil';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  gap: 24px;
  flex: 1;
`;

const IconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get('Blue')?.[100]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
`;

const LargeInputWrapper = styled.div`
  input {
    font-size: 22px !important;
    letter-spacing: 4px;
  }
`;

const ErrorMessage = styled.div`
  background: ${COLOR_SYSTEM.get('Red')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Red')?.[200]};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  max-width: 400px;
  white-space: pre-line;
`;

const stepStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '10px 0',
  textAlign: 'left',
};

const stepNumberStyle: React.CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13px',
  fontWeight: 600,
  flexShrink: 0,
};

const AUTH_METHOD_GUIDE: Record<string, { steps: string[] }> = {
  '2': { steps: [
    '이 화면을 잠시 나가주세요.',
    "카카오톡 채팅 목록에서 '카카오톡 지갑'의 알림을 확인하고 인증해주세요.",
    '인증 완료 후 다시 이 페이지로 돌아와주세요.',
  ]},
  '10': { steps: [
    'PASS 앱에서 인증을 완료해주세요.',
    '인증 완료 후 다시 이 페이지로 돌아와주세요.',
  ]},
  '0': { steps: [
    '네이버 앱에서 인증을 완료해주세요.',
    '인증 완료 후 다시 이 페이지로 돌아와주세요.',
  ]},
  '6': { steps: [
    '토스 앱에서 인증을 완료해주세요.',
    '인증 완료 후 다시 이 페이지로 돌아와주세요.',
  ]},
  '8': { steps: [
    '삼성패스에서 인증을 완료해주세요.',
    '인증 완료 후 다시 이 페이지로 돌아와주세요.',
  ]},
};

interface AuthPageProps {
  token: string;
  onStatusChange: (status: NhisStatus, data?: any) => void;
  onReload?: () => void;
  authMethod?: string | null;
  authMethodType?: AuthMethodType;
}

const AuthPage: React.FC<AuthPageProps> = ({ token, onStatusChange, onReload, authMethod, authMethodType }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificationNum, setCertificationNum] = useState('');

  const showToastAndRestart = (message: string | React.ReactNode) => {
    ToastManager.show({ content: message, duration: 5000 });
    onStatusChange('PENDING');
  };

  const isPhoneVerify = authMethodType === 'PHONE_VERIFY';

  const handleConfirm = async (overrideCertificationNum?: string) => {
    setLoading(true);
    setError(null);

    const submittedCertNum = isPhoneVerify ? (overrideCertificationNum ?? certificationNum) : undefined;

    const result = await confirmNhisAuth({
      token,
      ...(submittedCertNum ? { certificationNum: submittedCertNum } : {}),
    });

    // 인증번호 제출 직후 메모리에서 즉시 제거 (가이드 8.1 보안)
    if (isPhoneVerify) {
      setCertificationNum('');
    }

    if (result.error) {
      log(LogLevel.ERROR, 'AuthPage: 인증 확인 실패', result);
      setLoading(false);

      if (result.error === 7115) {
        // 미인증 상태 (Octover 480/483) - AuthPage 유지
        setError('아직 인증이 완료되지 않았습니다.\n인증 메시지를 받지 못하셨다면, 아래 "처음부터 다시 시도" 버튼을 눌러주세요.');
        return;
      }

      // PHONE_VERIFY 세션의 7117: detailCode=1000484(인증번호 오입력)만 재시도 가능.
      // 그 외(1000483/1000485/1000486)는 백엔드가 FAILED로 전이시키므로 처음으로 유도.
      if (isPhoneVerify && result.error === 7117 && result.data?.detailCode === '1000484') {
        setError(result.message || '인증번호가 올바르지 않습니다. 다시 입력해주세요.');
        return;
      }

      // 나머지 에러: detailCode별 환자 친화적 메시지 우선, 없으면 서버 메시지 fallback
      const detailCode = result.data?.detailCode;
      const title = detailCode ? NHIS_DETAIL_MESSAGES[detailCode] : null;
      const action = detailCode ? NHIS_DETAIL_ACTIONS[detailCode] : null;
      if (title && action) {
        showToastAndRestart(<>{title}<br/>{action}</>);
      } else {
        const detailMessage = result.data?.detailMessage;
        const message = detailMessage || result.message || '인증에 실패했습니다. 처음부터 다시 시도해주세요.';
        showToastAndRestart(message);
      }
      return;
    }

    // 성공. status는 응답 전용 판별자다(DB 상태 아님).
    setLoading(false);
    if (result.data?.status === 'NO_DATA') {
      // 조회 0건 종결: 백엔드가 이미 DELIVERED로 종결했다. MedicationListPage를 거치지 않고
      // 0건 화면으로 보낸다. 재인증을 유도하지 않는다 — 다시 해도 결과는 같다.
      onStatusChange('DELIVERED', { noData: true });
      return;
    }
    // 그 외(status 없음 등, 구버전 백엔드 포함): 기존대로 FETCHED로 전환.
    onStatusChange('FETCHED', { prescriptionGroups: result.data?.prescriptionGroups || [] });
  };

  const handleRestartWithConfirm = () => {
    // 모바일: 입력 중이던 인증번호 input이 포커스된 상태라면 blur 시켜 가상 키보드를 내려야
    // ConfirmPopup이 키보드 뒤로 숨지 않는다.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    ConfirmManager.show({
      title: '처음부터 다시 시도',
      body: '진행 중인 인증이 초기화됩니다.\n처음부터 다시 시작하시겠습니까?',
      backdropClose: true,
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={() => {
            ConfirmManager.hide();
            onStatusChange('PENDING');
          }}
        >
          확인
        </Button>,
      ],
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner size="Large" />
          <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[700]}>
            건강보험심사평가원에서 처방내역을 조회하고 있습니다...
          </Text>
          <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[500]}>
            최대 1분 정도 소요될 수 있습니다.
          </Text>
        </LoadingContainer>
      </Container>
    );
  }

  // PHONE_VERIFY: SMS 인증번호 입력 화면 (AUTH_METHOD_GUIDE 접근 전에 early return)
  if (isPhoneVerify) {
    const certNumValid = /^\d{4,6}$/.test(certificationNum);

    return (
      <Container>
        <Text textType="H2" color={COLOR_SYSTEM.get('Gray')?.[800]}>
          문자로 받은 인증번호를 입력해주세요
        </Text>

        <LargeInputWrapper style={{ width: '100%', maxWidth: '400px' }}>
          <Input
            placeholder="인증번호 (4~6자리)"
            value={certificationNum}
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            name="one-time-code"
            id="one-time-code"
            onChange={(e) => {
              const val = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6);
              setCertificationNum(val);
            }}
          />
        </LargeInputWrapper>

        {error && (
          <ErrorMessage>
            <Text textType="Body1" color={COLOR_SYSTEM.get('Red')?.[600]} style={{ lineHeight: '1.7' }}>
              {error.split('\n').map((line, i) => (
                <span key={i}>{i > 0 && <br />}{line}</span>
              ))}
            </Text>
          </ErrorMessage>
        )}

        <div style={{ width: '100%', maxWidth: '300px' }}>
          <Button
            buttonType="button"
            color="Primary"
            size="Large"
            type="Text"
            variant="Contained"
            onClick={() => handleConfirm()}
            disabled={!certNumValid}
            disabledColor={!certNumValid}
          >
            인증번호 확인
          </Button>
        </div>

        <div style={{
          marginTop: '24px',
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center',
        }}>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '8px', display: 'block' }}>
            인증번호를 받지 못하셨나요?
          </Text>
          <button
            type="button"
            onClick={handleRestartWithConfirm}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: COLOR_SYSTEM.get('Gray')?.[500],
              fontSize: '14px',
              textDecoration: 'underline',
            }}
          >
            처음부터 다시 시도하기
          </button>
        </div>
      </Container>
    );
  }

  const guide = authMethod ? AUTH_METHOD_GUIDE[authMethod] : AUTH_METHOD_GUIDE['2'];

  return (
    <Container>
      <Text textType="H2" color={COLOR_SYSTEM.get('Gray')?.[800]}>
        2단계: 본인인증을 진행해주세요
      </Text>

      <div style={{
        padding: '16px 20px',
        backgroundColor: COLOR_SYSTEM.get('Blue')?.[50],
        border: `1px solid ${COLOR_SYSTEM.get('Blue')?.[200]}`,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '400px',
      }}>
        {guide.steps.map((text, i) => (
          <div key={i} style={stepStyle}>
            <div style={{
              ...stepNumberStyle,
              backgroundColor: COLOR_SYSTEM.get('Blue')?.[500],
              color: '#ffffff',
            }}>
              {i + 1}
            </div>
            <Text textType="Body1" color={COLOR_SYSTEM.get('Blue')?.[700]} style={{ fontSize: '16px', paddingTop: '2px' }}>
              {text}
            </Text>
          </div>
        ))}
      </div>

      <div style={{
        padding: '14px 16px',
        backgroundColor: COLOR_SYSTEM.get('Gray')?.[50],
        border: `1px solid ${COLOR_SYSTEM.get('Gray')?.[200]}`,
        borderRadius: '10px',
        width: '100%',
        maxWidth: '400px',
      }}>
        <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ fontSize: '14px', lineHeight: '1.7' }}>
          인증 후 이 화면으로 돌아오기 어렵다면,
          <br />
          처음에 받으신 카카오톡 메시지를 다시 눌러주세요.
          <br />
          (진행 내역은 사라지지 않습니다)
        </Text>
      </div>

      <div style={{
        width: '100%',
        maxWidth: '400px',
        borderTop: `1px solid ${COLOR_SYSTEM.get('Gray')?.[200]}`,
        paddingTop: '24px',
      }}>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ display: 'block', fontSize: '18px', fontWeight: 600, marginBottom: '10px' }}>
          인증을 모두 마치고 돌아오셨나요?
        </Text>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ fontSize: '16px', marginBottom: '16px', lineHeight: '1.7' }}>
          위의 인증을 모두 완료하셨다면
          <br />
          아래 버튼을 눌러주세요.
        </Text>
      </div>

      {error && (
        <ErrorMessage>
          <Text textType="Body1" color={COLOR_SYSTEM.get('Red')?.[600]} style={{ lineHeight: '1.7' }}>
            {error.split('\n').map((line, i) => (
              <span key={i}>{i > 0 && <br />}{line}</span>
            ))}
          </Text>
        </ErrorMessage>
      )}

      <div style={{ width: '100%', maxWidth: '300px' }}>
        <Button
          buttonType="button"
          color="Primary"
          size="Large"
          type="Text"
          variant="Contained"
          onClick={() => handleConfirm()}
        >
          다음 단계로
        </Button>
      </div>

      <div style={{
        marginTop: '24px',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
      }}>
        <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]} style={{ fontSize: '14px', lineHeight: '1.7', marginBottom: '8px', display: 'block' }}>
          선택하신 인증방식에 어려움이 있으신가요?
        </Text>
        <button
          type="button"
          onClick={handleRestartWithConfirm}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: COLOR_SYSTEM.get('Gray')?.[500],
            fontSize: '14px',
            textDecoration: 'underline',
          }}
        >
          처음부터 다시 시도하기
        </button>
      </div>
    </Container>
  );
};

export default AuthPage;
